import Guid from "_shared/guids/guid";
import BookmarkToggle from "marketplace/bookmarks/_views/bookmark-toggle";
import {
  useCreateIndividualBookmarkMutation,
  useDeleteIndividualBookmarkMutation,
} from "marketplace/bookmarks/_api/bookmark-api";
import useIndividualBookmarkStatus from "marketplace/bookmarks/_hooks/use-individual-bookmark-status";
import { useSnackbar } from "_shared/_providers/snackbars/snackbars";

export interface IndividualBookmarkButtonProps {
  className?: string;
  individualId?: Guid;
  /** The result's name, which the toggle's accessible name repeats. */
  name: string;
}

export default function IndividualBookmarkButton(props: Readonly<IndividualBookmarkButtonProps>) {
  const { className, individualId, name } = props;

  const { enqueueSnackbar } = useSnackbar();

  // One person's bookmark, not the whole list: the hook puts the ids that every card on a results
  // page asks for in the same tick into one bulk request. Undefined while it loads, null for none.
  const { bookmark: existingBookmark, hasFailed, retry } = useIndividualBookmarkStatus(individualId);
  const [createIndividualBookmark, { isLoading: isCreating }] = useCreateIndividualBookmarkMutation();
  const [deleteIndividualBookmark, { isLoading: isDeleting }] = useDeleteIndividualBookmarkMutation();

  const isLoading = (existingBookmark === undefined && !hasFailed) || isCreating || isDeleting;

  async function handleBookmarkClick() {
    // Whether the person is bookmarked is unknown, so neither adding nor removing is safe. Ask again.
    if (hasFailed) {
      retry();
      return;
    }
    try {
      if (existingBookmark) {
        await deleteIndividualBookmark({
          bookmarkId: existingBookmark.id,
          individualId: existingBookmark.individualId,
        }).unwrap();
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!individualId) throw new Error("Bookmark Individual: Individual ID is undefined");
        await createIndividualBookmark({ individualId: individualId.value }).unwrap();
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  }

  return (
    <BookmarkToggle
      className={className}
      name={name}
      bookmarked={Boolean(existingBookmark)}
      disabled={!individualId || isLoading}
      onToggle={handleBookmarkClick}
    />
  );
}
